/**
 * Linmere New York: countdown for {TIMER} in the cart message band (Theme settings → Linmere · Cart).
 * Starts the first time the cart drawer opens in a browser session (sessionStorage), counts down from
 * data-seconds and stops at 00:00. Nothing happens to the cart at zero. Created 14 Oct 2026.
 */
const STORAGE_KEY = 'lnm_cart_timer_start';
let memoryStart = null;

function readStart() {
  try {
    const value = Number(sessionStorage.getItem(STORAGE_KEY));
    if (value > 0) return value;
  } catch (error) {}
  return memoryStart;
}

function saveStart(time) {
  memoryStart = time;
  try {
    sessionStorage.setItem(STORAGE_KEY, String(time));
  } catch (error) {}
}

function format(seconds) {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

class LnmCartTimer extends HTMLElement {
  #interval = null;
  #drawerObserver = null;
  #selfObserver = null;

  get #total() {
    return Math.max(1, parseInt(this.dataset.seconds, 10) || 600);
  }

  get #drawer() {
    return this.closest('theme-drawer');
  }

  connectedCallback() {
    // Digits keep the same width while the countdown ticks.
    this.style.fontVariantNumeric = 'tabular-nums';

    // The cart re-render (morph) can rewrite the text; put the live value back right away.
    this.#selfObserver = new MutationObserver(() => this.#render());
    this.#selfObserver.observe(this, { childList: true, characterData: true, subtree: true });

    if (readStart() || this.#isDrawerOpen()) {
      this.#start();
      return;
    }

    this.#render();
    const drawer = this.#drawer;
    if (!drawer) return;
    this.#drawerObserver = new MutationObserver(() => {
      if (this.#isDrawerOpen()) this.#start();
    });
    this.#drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'], subtree: true });
  }

  disconnectedCallback() {
    clearInterval(this.#interval);
    this.#interval = null;
    this.#drawerObserver?.disconnect();
    this.#selfObserver?.disconnect();
  }

  #isDrawerOpen() {
    const drawer = this.#drawer;
    return Boolean(drawer && (drawer.hasAttribute('open') || this.closest('dialog')?.open));
  }

  #start() {
    this.#drawerObserver?.disconnect();
    this.#drawerObserver = null;
    if (!readStart()) saveStart(Date.now());
    this.#render();
    if (!this.#interval && this.#remaining() > 0) {
      this.#interval = setInterval(() => this.#render(), 1000);
    }
  }

  #remaining() {
    const start = readStart();
    if (!start) return this.#total;
    return Math.max(0, this.#total - Math.floor((Date.now() - start) / 1000));
  }

  #render() {
    const remaining = this.#remaining();
    const text = format(remaining);
    if (this.textContent !== text) this.textContent = text;
    if (remaining === 0 && this.#interval) {
      clearInterval(this.#interval);
      this.#interval = null;
    }
  }
}

if (!customElements.get('lnm-cart-timer')) customElements.define('lnm-cart-timer', LnmCartTimer);
